import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { portalApi, setPortalToken, type PortalDashboard } from '@/api/portal.api';
import type { DeskTicket } from '@/types/desk';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function PortalDashboardPage() {
  const [dashboard, setDashboard] = useState<PortalDashboard | null>(null);
  const [tickets, setTickets] = useState<DeskTicket[]>([]);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [reply, setReply] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    const token = localStorage.getItem('portal_token');
    if (!token) return;
    setPortalToken(token);
    try {
      const [d, t] = await Promise.all([portalApi.dashboard(), portalApi.listTickets()]);
      setDashboard(d);
      setTickets(t);
    } catch (err) {
      showApiError(err, 'Session expired');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTicket = async () => {
    try {
      await portalApi.createTicket({ subject, description });
      notify.success('Ticket created');
      setSubject('');
      setDescription('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create ticket');
    }
  };

  const sendReply = async (ticketId: number) => {
    const text = reply[ticketId];
    if (!text?.trim()) return;
    try {
      await portalApi.addComment(ticketId, text);
      notify.success('Reply sent');
      load();
    } catch (err) {
      showApiError(err, 'Reply failed');
    }
  };

  if (!dashboard) {
    return <div className="p-8 text-slate-500 dark:text-slate-400">Loading portal…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="border-b border-slate-200 bg-white px-6 py-4 flex justify-between items-center dark:border-slate-800 dark:bg-slate-900">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Welcome, {dashboard.customerName}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{dashboard.openTicketCount} open tickets</p>
        </div>
        <Link to="/portal/login" className="text-sm text-primary-600 dark:text-primary-400" onClick={() => { localStorage.removeItem('portal_token'); setPortalToken(null); }}>
          Sign out
        </Link>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-8">
        <section className="card p-4 space-y-3">
          <h2 className="font-semibold">New support ticket</h2>
          <Input label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <Textarea label="Description" rows={3} placeholder="Describe your issue" value={description} onChange={(e) => setDescription(e.target.value)} />
          <Button onClick={createTicket}>Submit ticket</Button>
        </section>

        <section>
          <h2 className="font-semibold mb-3">Your tickets</h2>
          <ul className="space-y-4">
            {tickets.map((t) => (
              <li key={t.id} className="card p-4">
                <p className="font-medium">{t.subject}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t.ticketNumber}</p>
                <div className="mt-3 flex gap-2">
                  <Input
                    className="flex-1"
                    placeholder="Reply…"
                    value={reply[t.id] ?? ''}
                    onChange={(e) => setReply((r) => ({ ...r, [t.id]: e.target.value }))}
                  />
                  <Button variant="secondary" onClick={() => sendReply(t.id)}>Send</Button>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="font-semibold mb-3">Recent invoices</h2>
          <ul className="space-y-2 text-sm">
            {dashboard.invoices.map((inv) => (
              <li key={inv.id} className="flex justify-between border-b py-2">
                <span>{inv.invoiceNumber}</span>
                <span>{inv.statusCode}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}
